import axios from 'axios';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Mobile } from '../../../App';
import Banner from '../../../components/common/Banner';
import { SidebarModal } from '../../../components/common/Sidebar/Sidebar';
import SidebarNav from '../../../components/common/SidebarNav';
import { useOnClickOutside } from '../../../helpers/helperFunctions';
import { useWindowWide } from '../../../helpers/wideScreen';
import { P2 } from '../../../styles/FontStyles';
import {
  ArrowPolygon,
  Flexed,
  UserImageCircle
} from '../../../styles/GlobalComponents';
import { colors, globalImages } from '../../../styles/GlobalStyles';
import {
  Icon,
  IconMenu,
  IconNotifications,
  IconTelegramNoBorder
} from '../../../styles/Icons';
import { FlexCenter } from '../../../styles/stylesStyled';
import Notifications from '../../../views/Notifications/Notifications';

import { DASHBOARD, MY_HUBS } from '../../../routes/routes';
import {
  MainNav,
  TopNavigation,
  TopNavigationMain
} from '../BackofficeLayoutStyled';

import { AnimatePresence } from 'framer-motion';
import { useAppSelector } from '../../../app/hooks';
import { getNotifications } from '../../../slices/notifications-slice';
import { ProfileDropdown } from './ProfileDropdown';
import { ShowHide } from './ShowHide';
import { UserTypes } from '../../../types/loginTypes';
import { getCurrentLicense } from '../../../slices/auth-slice';

const APPLICATION_SERVER_KEY = process.env.REACT_APP_APPLICATION_SERVER_KEY;

export const TopNav = () => {
  const { userInfo } = useAppSelector((state) => state.auth);
  const wide = useWindowWide(800);
  const [sidebar, setSidebar] = useState(false);
  const ref = useRef(null);
  const history = useHistory();
  const [menuIsOpen, setMenuIsOpen] = useState(false);
  const dispatch = useDispatch();

  const [notificationsModal, setNotificationsModal] = useState(false);
  const { t } = useTranslation('layout');

  useOnClickOutside(ref, () => menuIsOpen && setMenuIsOpen(false));

  useEffect(() => {
    dispatch(getCurrentLicense());
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker
        .register('/sw.js')
        .then(
          function (registration) {
            // Registration was successful
            // console.log(
            //   'ServiceWorker registration successful with scope: ',
            //   registration.scope
            // );
            subscribe();
          },
          function (err) {
            // registration failed :(
            // console.log('ServiceWorker registration failed: ', err);
          }
        )
        .catch(() => {
          // console.log('----');
        });
    }
  }, []);

  async function subscribe() {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.ready.then(function (reg) {
        let token = userInfo?.token?.accessToken;
        if (!token) return;
        const config = {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
            'Access-Control-Allow-Origin': '*'
          }
        };

        reg?.pushManager
          ?.subscribe({
            userVisibleOnly: true,
            applicationServerKey: APPLICATION_SERVER_KEY
          })
          .then(function (sub) {
            // console.log('Endpoint URL: ', sub.endpoint);
            const data = JSON.parse(JSON.stringify(sub));
            axios.post(
              `/user/notification/web/device`,
              {
                endpoint: data.endpoint,
                p256dh: data.keys.p256dh,
                auth: data.keys.auth
              },
              config
            );
          })
          .catch(function (e) {
            if (Notification.permission === 'denied') {
              console.log('Permission for notifications was denied');

              axios.delete(`/user/notification/web/device`, config);
            } else {
              console.error('Unable to subscribe to push', e);
            }
          });
      });
    }
  }

  return (
    <AnimatePresence>
      {ShowHide() && (
        <TopNavigation
          key='TopNavigation'
          initial={{ transform: 'translateY(-70px)' }}
          animate={{ transform: 'translateY(0px)' }}
          exit={{ transform: 'translateY(-70px)' }}
          transition={{
            duration: 0.2
          }}>
          <TopNavigationMain>
            <Flexed center>
              <Mobile>
                <Icon
                  mr='10'
                  pointer
                  onClick={() => {
                    setSidebar(!sidebar);
                  }}
                  color={colors.neutral900}
                  stroke>
                  <IconMenu />
                </Icon>
              </Mobile>
              <img
                src={wide ? globalImages.appLogo : globalImages.logoOnlyLetter}
                alt='logo'
                style={{
                  marginRight: 40,
                  height: 40,
                  cursor: 'pointer'
                }}
                onClick={() =>
                  history.push(
                    userInfo.userType === UserTypes.Regular
                      ? DASHBOARD
                      : MY_HUBS
                  )
                }
              />
            </Flexed>

            <MainNav>
              <a
                href='https://t.me/travelution_official'
                target='_blank'
                rel='noreferrer'>
                <Icon
                  pointer
                  w='34px'
                  ml='20'
                  mr='20'
                  mlSM='10'
                  color={colors.neutral900}>
                  <IconTelegramNoBorder />
                </Icon>
              </a>

              <div style={{ position: 'relative' }}>
                <Banner
                  unseen={userInfo.unseenNotificationCount}
                  notification={true}
                />

                <Icon
                  onClick={() => {
                    setNotificationsModal(!notificationsModal);
                    dispatch(getNotifications());
                  }}
                  pointer
                  mr='20'
                  mrSM='10'
                  color={colors.neutral900}>
                  <IconNotifications />
                </Icon>
              </div>

              <FlexCenter ref={ref}>
                <div
                  onClick={() => {
                    setMenuIsOpen(!menuIsOpen);
                  }}>
                  <UserImageCircle
                    image={userInfo?.user?.profileImageUrl}
                    name={userInfo?.user?.name}
                    surname={userInfo?.user?.surname}
                    size='40px'
                    fontSize='16px'
                    margin={wide ? '0 16px 0 0' : '0'}
                  />
                </div>

                <div
                  style={{
                    fontSize: '16px',
                    lineHeight: '34px',
                    position: 'relative'
                  }}>
                  <div
                    style={{
                      display: wide ? 'flex' : 'none',
                      alignItems: 'center',
                      cursor: 'pointer'
                    }}
                    onClick={() => {
                      setMenuIsOpen(!menuIsOpen);
                    }}>
                    <P2 mr='10'>{userInfo?.user?.name}</P2>

                    <ArrowPolygon direction={menuIsOpen ? 'up' : 'down'} />
                  </div>
                  <div>
                    <ProfileDropdown
                      menuIsOpen={menuIsOpen}
                      userInfo={userInfo}
                      setMenuIsOpen={setMenuIsOpen}
                    />
                  </div>
                </div>
              </FlexCenter>
            </MainNav>
          </TopNavigationMain>
          <SidebarNav open={sidebar} onClose={setSidebar} />
        </TopNavigation>
      )}
      <SidebarModal
        isOpen={notificationsModal}
        setOpen={setNotificationsModal}
        title={t('notifications.title')}
        sidebarWidth={'508px'}>
        <Notifications setNotificationsModal={setNotificationsModal} />
      </SidebarModal>
    </AnimatePresence>
  );
};
